import type { Post } from '@/@types/post'
import LikeIcon from '@/assets/like.svg?react'
import { LIKE_LOTTIE_DATA_QUERY_OPTIONS } from '@/utils/fetchers/manifest'
import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { get, isEqual } from 'lodash-es'
import type { AnimationItem } from 'lottie-web'
import { type HTMLAttributes, type SyntheticEvent, memo, useRef } from 'react'
import Lottie from 'react-lottie-player'
import styles from './DynamicLikeIcon.module.css'

export interface DynamicLikeIconProps extends HTMLAttributes<HTMLDivElement> {
	data: Post
	onClick?: (e: SyntheticEvent<Element>) => void
}

export default memo(function DynamicLikeIcon({ data, className, ...props }: DynamicLikeIconProps) {
	const liked = !!data.liked
	const playerRef = useRef<AnimationItem | null>(null)

	const { data: lottieData } = useQuery({
		...LIKE_LOTTIE_DATA_QUERY_OPTIONS,
		select: (likeLottieDataMap) => get(likeLottieDataMap, data.likeIcon!),
		enabled: !!data.likeIcon,
	})

	if (!data.likeIcon || !lottieData) {
		return (
			<LikeIcon
				className={clsx(styles.icon, className)}
				{...(props as HTMLAttributes<SVGSVGElement>)}
			/>
		)
	}

	return (
		<Lottie
			play
			loop={false}
			ref={playerRef}
			animationData={lottieData.data}
			segments={liked ? lottieData.selectSegment : lottieData.unselectSegment}
			goTo={liked ? lottieData.unselectStart : lottieData.selectStart}
			data-liked={liked}
			className={clsx(styles.lottie, className)}
			{...props}
		/>
	)
}, isEqual)
